import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MapleLeaf } from '@/components/common/MapleLeaf';

export default function CompleteRegistrationPage() {
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user, session, loading: authLoading } = useAuth();
  const { updateProfile } = useProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!authLoading && !session) {
      toast({
        title: 'Invalid or expired link',
        description: 'Please sign up again.',
        variant: 'destructive',
      });
      navigate('/auth');
    }
  }, [session, authLoading, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !password) {
      toast({ title: "All fields are required.", variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    setIsLoading(true);

    try {
      const { error: userError } = await supabase.auth.updateUser({ password });
      if (userError) throw userError;

      const { success, error: profileError } = await updateProfile({ full_name: fullName });
      if (!success || profileError) throw profileError || new Error("Failed to update profile.");

      if (user?.id) {
        const { error: functionError } = await supabase.functions.invoke('send-welcome-email', {
          body: { userId: user.id },
        });
        if (functionError) {
          console.error("Failed to send welcome email:", functionError);
        }
      }

      toast({
        title: "Registration Complete!",
        description: "Welcome to CanXP AI. You are now being redirected.",
      });

      navigate('/');

    } catch (error: any) {
      toast({
        title: 'Registration Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="mb-6 flex flex-col items-center">
        <MapleLeaf size={56} className="text-canada-red drop-shadow-lg mb-2" />
        <span className="text-lg font-bold bg-gradient-to-r from-canada-red to-canada-red/70 bg-clip-text text-transparent tracking-wide">
          CanXP AI
        </span>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Your Account</CardTitle>
          <CardDescription>Just one more step to get started.</CardDescription>
          {user?.email && (
            <p className="text-sm text-muted-foreground mt-2">
              Welcome <strong>{user.email}</strong>! Please create a password to finish setting up your account.
            </p>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="fullName">Full Name</label>
              <Input
                id="fullName"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password">Password</label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">Must be at least 6 characters.</p>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Complete Registration'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}